import type { Session } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { PasswordRecoveryPage } from "./PasswordRecoveryPage";
import type { AppRole, UserProfile } from "./types";

interface AuthValue {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  profile: UserProfile | null;
  requestPasswordReset(email: string): Promise<string | null>;
  signIn(email: string, password: string): Promise<string | null>;
  signOut(): Promise<void>;
}

type RecoveryStatus = "idle" | "active" | "complete";

const AuthContext = createContext<AuthValue | null>(null);

function hasRecoveryToken() {
  if (typeof window === "undefined") return false;
  const locationValue = `${window.location.search}#${window.location.hash}`;
  return locationValue.includes("type=recovery");
}

async function loadProfile(userId: string): Promise<UserProfile | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from("profiles")
    .select("id,display_name,role,organization,is_active")
    .eq("id", userId)
    .single();

  if (error || !data?.is_active) return null;

  return {
    id: data.id,
    displayName: data.display_name,
    role: data.role as AppRole,
    organization: data.organization,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus>(() =>
    hasRecoveryToken() ? "active" : "idle",
  );

  useEffect(() => {
    if (!supabase) return;
    let active = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      setProfile(data.session ? await loadProfile(data.session.user.id) : null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryStatus("active");
      setSession(nextSession);
      if (!nextSession) {
        setProfile(null);
      } else {
        const p = await loadProfile(nextSession.user.id);
        if (active) setProfile(p);
      }
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) return "Supabase todavía no está configurado.";

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return "No fue posible iniciar sesión. Verifica el correo y la contraseña.";

    const nextProfile = data.user ? await loadProfile(data.user.id) : null;
    if (!nextProfile) {
      await supabase.auth.signOut();
      setSession(null);
      setProfile(null);
      return "La cuenta no tiene un perfil activo.";
    }

    setSession(data.session);
    setProfile(nextProfile);
    return null;
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    if (!supabase) return "Supabase todavía no está configurado.";

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });

    if (error) return "No fue posible enviar el correo de recuperación. Inténtalo más tarde.";
    return null;
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    if (!supabase) return "Supabase todavía no está configurado.";

    const { error } = await supabase.auth.updateUser({ password });
    if (error) return "No fue posible actualizar la contraseña. Solicita un enlace nuevo.";

    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setRecoveryStatus("complete");
    return null;
  }, []);

  const signOut = useCallback(async () => {
    await supabase?.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      configured: isSupabaseConfigured,
      loading,
      session,
      profile,
      requestPasswordReset,
      signIn,
      signOut,
    }),
    [loading, session, profile, requestPasswordReset, signIn, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {recoveryStatus === "idle" ? (
        children
      ) : (
        <PasswordRecoveryPage
          completed={recoveryStatus === "complete"}
          loading={loading}
          onContinue={() => setRecoveryStatus("idle")}
          onUpdate={updatePassword}
          ready={Boolean(session)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe utilizarse dentro de AuthProvider");
  return value;
}
